import cn from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';

import SearchDocumentFilters from '@/components/shared/searchTree/SearchDocumentFilters';
import SearchTree from '@/components/shared/searchTree/SearchTreeField';
import TreeSearchEmptyState from '@/components/shared/searchTree/TreeSearchEmptyState';
import { ROUTES } from '@/configs/routes';
import { useAutoOpenDocumentsFolder } from '@/hooks/useAutoOpenDocumentsFolder';
import { useLocalStore } from '@/hooks/useLocalStore';
import DocumentNotesDrawer from '@/pages/OrganizationPageDocumentPreview/Content/DocumentNotesDrawer';
import { OrganizationDocumentPreviewPageNotesStore } from '@/stores/local/pages/OrganizationDocumentPreviewPageStore/NotesStore';
import type { PdfDocumentNote } from '@/stores/local/shared/PdfNotesStore';
import { useSidebarDocumentsStore } from '@/stores/local/sidebar/SidebarDocumentsStore';
import { useDevices } from '@/utils/devices';

import DocumentsTree from './DocumentsTree';
import Popups from './Popups';
import SearchContent from './SearchContent';

import s from './DocumentsContent.module.scss';

type DocumentsContentProps = {
  className?: string;
};

const DocumentsContent: React.FC<DocumentsContentProps> = ({ className }) => {
  const store = useSidebarDocumentsStore();
  const navigate = useNavigate();
  const { isCompactViewport } = useDevices();
  const { id, documentId } = useParams();
  const { pathname } = useLocation();
  const organizationId = Number(id);
  const normalizedOrganizationId = Number.isFinite(organizationId) ? organizationId : undefined;
  const drawerDocumentId = React.useMemo(() => observable.box<string | null>(null), []);
  const drawerDocumentName = React.useMemo(() => observable.box<string>('Документ'), []);
  const notesDrawerStore = useLocalStore(
    () =>
      new OrganizationDocumentPreviewPageNotesStore({
        organizationId: String(normalizedOrganizationId ?? 0),
        getDocumentId: () => drawerDocumentId.get() ?? undefined,
        getCurrentPage: () => 1,
        getDocumentName: () => drawerDocumentName.get() || undefined,
        api: store.pdfNotesApi,
      }),
  );

  const isChatMode = React.useMemo(() => {
    return Boolean(matchPath(ROUTES.organization.documents.chat.path, pathname));
  }, [pathname]);

  const withSelection = isChatMode || store.isContextSelectionEnabled;
  const isPreviewRoute = React.useMemo(() => {
    return Boolean(
      matchPath({ path: ROUTES.organization.documents.document.documentPreview.path, end: false }, pathname),
    );
  }, [pathname]);

  useAutoOpenDocumentsFolder(store, normalizedOrganizationId);
  const openNotesRequest = store.pdfNotesIndex.openNotesRequest;

  React.useEffect(() => {
    if (!openNotesRequest) {
      return;
    }

    const isSameDocumentPreviewOpen = Boolean(
      isPreviewRoute && documentId && String(documentId) === String(openNotesRequest.documentId),
    );

    if (isSameDocumentPreviewOpen) {
      return;
    }

    drawerDocumentId.set(String(openNotesRequest.documentId));
    drawerDocumentName.set(openNotesRequest.name?.trim() || 'Документ');
    notesDrawerStore.backToList();
    notesDrawerStore.openDrawer();
    store.pdfNotesIndex.consumeOpenNotesRequest(openNotesRequest.token);
  }, [
    documentId,
    drawerDocumentId,
    drawerDocumentName,
    isPreviewRoute,
    notesDrawerStore,
    openNotesRequest,
    store.pdfNotesIndex,
  ]);

  const handleOpenPageFromNote = React.useCallback(
    (note: PdfDocumentNote) => {
      if (!id) {
        return;
      }

      notesDrawerStore.closeDrawer();
      store.pdfNotesIndex.requestOpenNotes(note.documentId, note.caption);
      navigate(
        ROUTES.organization.documents.document.documentPreview.create(
          String(id),
          note.documentId,
          String(note.pageNumber),
        ),
        {
          state: {
            activePdfNoteId: note.id,
            openPdfNotes: true,
          },
        },
      );
    },
    [id, navigate, notesDrawerStore, store.pdfNotesIndex],
  );

  const {
    searchDocumentStore,
    popups: { uploadDocumentPopup, createFolderPopup },
  } = store;

  /** Корень дерева и шапка — только глобальный `can_create`. Личное: `can_create_in_personal` в меню папок внутри личной ветки (FolderActions). */
  const canCreateFolderFromHeader = store.permissionsStore.canCreate();
  const canUploadDocumentFromHeader = store.permissionsStore.canUploadToPersonal();

  return (
    <>
      <div className={cn(s.root, className)}>
        <div className={s.root__header}>
          <SearchTree
            store={searchDocumentStore}
            documentPopup={uploadDocumentPopup}
            folderPopup={createFolderPopup}
            canCreateFolder={canCreateFolderFromHeader}
            canUploadDocument={canUploadDocumentFromHeader}
          />
          <SearchDocumentFilters store={searchDocumentStore} />
        </div>
        <div className={s.root__content}>
          {searchDocumentStore.isShowEmptySearchState ? (
            <TreeSearchEmptyState onReset={searchDocumentStore.reset} />
          ) : searchDocumentStore.isShowSearchContentLoaded || searchDocumentStore.isShowSearchContentWithError ? (
            <SearchContent withSelection={withSelection} />
          ) : (
            <DocumentsTree withSelection={withSelection} />
          )}
        </div>
      </div>
      <Popups />
      <DocumentNotesDrawer store={notesDrawerStore} isPhone={isCompactViewport} onOpenPage={handleOpenPageFromNote} />
    </>
  );
};

export default observer(DocumentsContent);
